import { Brand } from '@axo/config/@types/Brand.types';
import type { consent } from '@axo/shared/data-access/types';
import {
  ConfigType,
  PartnerConsentChanges,
} from '@axo/shared/data-access/types/consent';
import { MarketCountryCode } from '@axo/shared/types';
import { IAPI } from '../../lib/models/api';
import { send } from '../../lib/send';

export const getConsentConfig =
  ({ url: { service: baseUrl } }: Pick<IAPI, 'url'>) =>
  (params: {
    country?: MarketCountryCode;
    configType?: ConfigType;
    brandID?: Brand | 'GLOBAL';
  }) => {
    const url = new URL('/consent/v2/config/snapshot', baseUrl);
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, value);
      }
    }

    return send<void, consent.ConsentConfigSnapshot[]>({
      baseURL: baseUrl,
      path: `${url.pathname}${url.search}`,
      method: 'GET',
    });
  };

export const getConsent =
  ({ url: { service: baseUrl }, token }: IAPI) =>
  async (params: {
    country?: MarketCountryCode;
    customerID?: string;
    configType?: ConfigType;
  }): Promise<consent.ConsentConfigData[]> => {
    const url = new URL('/consent/v2/consent', baseUrl);

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, value);
      }
    }

    return send({
      baseURL: baseUrl,
      token,
      path: `${url.pathname}${url.search}`,
      method: 'GET',
    });
  };

export const sendMasterConsent =
  ({ url: { service: baseUrl }, token }: IAPI) =>
  async (
    data: consent.ConsentConfigData,
    options?: { keepalive?: boolean; signal?: AbortSignal }
  ) =>
    send({
      baseURL: baseUrl,
      token,
      path: '/consent/v1/consent',
      method: 'POST',
      body: data,
      keepalive: options?.keepalive,
      signal: options?.signal,
    });

export const getConsentPartners =
  ({ url: { service: baseUrl }, token }: IAPI) =>
  async () =>
    send<void, consent.Partner[]>({
      baseURL: baseUrl,
      token,
      path: '/consent/v1/partner',
      method: 'GET',
    });

export const getConsentItemsByCustomer =
  ({ url: { service: baseUrl }, token }: IAPI) =>
  async (params: { country?: MarketCountryCode; customerID?: string }) => {
    const url = new URL('/consent/v2/consent/items', baseUrl);

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, value);
      }
    }

    return send<void, consent.ConsentItems>({
      baseURL: baseUrl,
      token,
      path: `${url.pathname}${url.search}`,
      method: 'GET',
    });
  };

export const updateConsentItemsByCustomer =
  ({ url: { service: baseUrl }, token }: IAPI) =>
  async ({
    country,
    customerID,
    patchData,
  }: {
    country?: MarketCountryCode;
    customerID?: string;
    patchData?: PartnerConsentChanges;
  }) =>
    send({
      baseURL: baseUrl,
      token,
      path: `/consent/v1/consent/${country}/${customerID}`,
      method: 'PATCH',
      body: patchData,
    });
